// Inventory Performance Metrics — one page, tab strip matching PRD sub-features.
// Composition rule: this module renders what Amazon publishes + what
// Inventory Health has already classified. Nothing is inferred here.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import * as api from './services/inventoryPerformanceApi';
import styles from './InventoryPerformance.module.css';

// Storage report returns rupees directly (not minor units) so we inline
// `₹` formatting where needed rather than dragging a helper through props.
const useEndpoint = (fetchFn) => {
  const authCtx = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, data: null, freshness: null, error: null });
  const reload = useCallback(async () => {
    if (!authCtx?.token) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const body = await fetchFn(authCtx.token);
      setState({ loading: false, data: body.data, freshness: body.freshness, error: null });
    } catch (e) {
      setState({ loading: false, data: null, freshness: null, error: e.message });
    }
  }, [authCtx?.token, fetchFn]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
};

// #49 Data-freshness banner. Renders when any source is missing / not
// completed / older than 24h. Applies the "newer report date wins"
// framing — highlights the OLDEST source, since that's what caps the
// view's freshness. Silent when everything is fresh.
const FreshnessBanner = ({ freshness }) => {
  if (!freshness) return null;
  const sources = freshness.sources || {};
  const now = Date.now();
  const stale = [];
  const missing = [];
  let newestMs = 0;
  let oldestMs = Infinity;
  Object.entries(sources).forEach(([rt, s]) => {
    if (!s || s.status !== 'completed' || !s.updated_at) {
      missing.push(rt);
      return;
    }
    const ms = new Date(s.updated_at).getTime();
    if (Number.isNaN(ms)) return;
    if (ms > newestMs) newestMs = ms;
    if (ms < oldestMs) oldestMs = ms;
    const ageHrs = (now - ms) / 3_600_000;
    if (ageHrs > 24) {
      stale.push({ rt, ageHrs: Math.round(ageHrs) });
    }
  });
  if (missing.length === 0 && stale.length === 0) return null;
  return (
    <div className={`${styles.banner} ${styles.bannerWarn}`}>
      <b>Partial data view.</b>
      {missing.length > 0 && (
        <> Reports pending: <span className={styles.mono}>{missing.join(', ')}</span>.</>
      )}
      {stale.length > 0 && (
        <> Older than 24h: <span className={styles.mono}>
          {stale.map((x) => `${x.rt} (${x.ageHrs}h)`).join(', ')}
        </span>.</>
      )}
      {newestMs > 0 && oldestMs < Infinity && newestMs !== oldestMs && (
        <> Newest report is <b>{Math.round((newestMs - oldestMs) / 3_600_000)}h</b> ahead
          of oldest — figures reflect the newer view where available.</>
      )}
    </div>
  );
};

const Tile = ({ label, value, hint, accent }) => (
  <div className={`${styles.tile} ${accent ? styles[`tile_${accent}`] : ''}`}>
    <div className={styles.tileLabel}>{label}</div>
    <div className={styles.tileValue}>{value ?? '—'}</div>
    {hint && <div className={styles.tileHint}>{hint}</div>}
  </div>
);

const causeChipCls = (cause) => {
  if (cause === 'over_ordering') return styles.causeOver;
  if (cause === 'demand_problem') return styles.causeDemand;
  return styles.causeUnknown;
};

// ── Overview ────────────────────────────────────────────────────────────────

const OverviewPanel = ({ onTabClick }) => {
  const { loading, data, freshness, error } = useEndpoint(api.getOverview);
  if (loading && !data) return <div className={styles.muted}>Loading overview…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const {
    storage_bill, account_measures, totals,
    reconciliation, storage_reconciliation,
  } = data;
  return (
    <div>
      {/* #49 Freshness banner — surfaces when any source is missing/stale;
          applies "newer report date wins" framing. */}
      <FreshnessBanner freshness={freshness} />
      {/* #33 IPI reconciliation banner — mismatches between account tiles
          and product-list counts. Every finding names the measure. */}
      {reconciliation && !reconciliation.passed && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          Reconciliation issue: {reconciliation.findings.map((f, i) => (
            <span key={i}>
              {i > 0 ? '; ' : ''}
              <b>{f.measure.replace(/_/g, ' ')}</b>
              {' '}implies {f.expected_count} SKUs but list shows {f.list_count}
            </span>
          ))}
        </div>
      )}
      {/* #40 storage reconciliation */}
      {storage_reconciliation && !storage_reconciliation.passed && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          Storage totals don't reconcile: account bill
          {' '}₹{storage_reconciliation.account_total.toLocaleString('en-IN')}
          {' '}vs sum-of-rows ₹{storage_reconciliation.sum_of_rows.toLocaleString('en-IN')}
          {' '}(diff ₹{storage_reconciliation.difference.toLocaleString('en-IN')})
        </div>
      )}
      <div className={styles.tileGrid}>
        <Tile label="Next-month storage bill" value={`₹${storage_bill.next_month_total.toLocaleString('en-IN')}`} accent="warn" />
        <Tile label="Surcharge share of bill" value={`${Math.round(storage_bill.surcharge_share * 100)}%`}
              accent={storage_bill.surcharge_share > 0.3 ? 'critical' : null}
              hint="Long-term storage surcharges vs base fee" />
        <Tile label="Excess SKUs" value={totals.excess_skus} accent="warn" />
        <Tile label="Stranded SKUs" value={totals.stranded_skus}
              hint={`${totals.stranded_units} units`} accent="critical" />
        <Tile label="Aging SKUs (at/near charged)" value={totals.aging_skus} />
        <Tile label="Estimated LTSF next charge" value={`₹${totals.estimated_ltsf.toLocaleString('en-IN')}`} />
      </div>

      <h4 className={styles.sectionTitle}>Amazon inventory measures</h4>
      <div className={styles.tileGrid}>
        {['sell_through', 'excess_share', 'stranded_share', 'in_stock_rate', 'low_inventory_fee_status'].map((k) => {
          const m = account_measures[k] || {};
          const label = k.replace(/_/g, ' ');
          if (m.state === 'accumulating') {
            const from = new Date(m.available_from).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
            return <Tile key={k} label={label} value="accumulating"
                         hint={`Amazon measures the last 90 days — available from ${from}`} />;
          }
          if (m.state === 'unavailable') {
            return <Tile key={k} label={label} value="unavailable"
                         hint={m.reason || ''} />;
          }
          // Sell-through is Amazon's ratio (can exceed 1); the shares are fractions.
          const value = m.value == null ? '—'
            : k === 'sell_through' ? Number(m.value).toFixed(2)
            : (m.value <= 1 ? `${(m.value * 100).toFixed(1)}%` : m.value);
          const hint = m.threshold != null
            ? `Amazon threshold: ${(m.threshold * 100).toFixed(0)}%`
            : (m.threshold_available === false ? 'threshold not published' : null);
          // #32 clickthrough — measure jumps to owning tab with a filter
          const ownedBy = k === 'excess_share' ? 'excess'
            : k === 'stranded_share' ? 'stranded'
            : k === 'sell_through' ? 'sell-through'
            : null;
          const clickable = ownedBy && m.value != null;
          return (
            <div key={k}
                 className={`${styles.tile} ${clickable ? styles.tileClickable : ''} ${
                   m.state === 'below_threshold' ? styles.tile_critical :
                   m.state === 'passing' ? styles.tile_ok : ''}`}
                 onClick={clickable ? () => onTabClick(ownedBy) : undefined}
                 role={clickable ? 'button' : undefined}
                 tabIndex={clickable ? 0 : undefined}>
              <div className={styles.tileLabel}>{label}</div>
              <div className={styles.tileValue}>{value}</div>
              {hint && <div className={styles.tileHint}>{hint}</div>}
              {clickable && <div className={styles.tileHint}>› View SKUs</div>}
            </div>
          );
        })}
      </div>

      <h4 className={styles.sectionTitle}>Click through to detail</h4>
      <div className={styles.sliceList}>
        <SliceRow label="Excess Inventory" hint="Products holding more stock than the seller's cycle needs"
                  onClick={() => onTabClick('excess')} count={totals.excess_skus} />
        <SliceRow label="Stranded Inventory" hint="Stock at Amazon with no working listing"
                  onClick={() => onTabClick('stranded')} count={totals.stranded_skus} />
        <SliceRow label="Inventory Aging" hint="SKUs at or approaching charged age bands"
                  onClick={() => onTabClick('aging')} count={totals.aging_skus} />
        <SliceRow label="Storage Cost" hint="Next-month attribution per SKU"
                  onClick={() => onTabClick('storage')} />
      </div>
    </div>
  );
};

const SliceRow = ({ label, hint, count, onClick }) => (
  <div className={styles.sliceRow} onClick={onClick} role="button" tabIndex={0}>
    <div className={styles.sliceMain}>
      <div className={styles.sliceLabel}>{label}</div>
      <div className={styles.sliceHint}>{hint}</div>
    </div>
    <div className={styles.sliceValues}>
      {count != null && <span className={styles.sliceCount}>{count} SKU{count === 1 ? '' : 's'}</span>}
      <span className={styles.chevron}>›</span>
    </div>
  </div>
);

// ── Excess ─────────────────────────────────────────────────────────────────

const ExcessPanel = ({ initialCauseFilter }) => {
  const { loading, data, error } = useEndpoint(api.getExcess);
  const [groupByAsin, setGroupByAsin] = useState(false);
  const [causeFilter, setCauseFilter] = useState(initialCauseFilter || 'all');
  const [expanded, setExpanded] = useState(new Set());
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const { total_excess_skus, totals, rows, assumptions } = data;
  // #67 client-side cause filter tied to url ?cause=demand_problem etc.
  const filtered = causeFilter === 'all'
    ? rows
    : rows.filter((r) => r.cause === causeFilter);

  // #8 group SKU rows under parent ASIN
  const grouped = groupByAsin ? (() => {
    const byAsin = new Map();
    for (const r of filtered) {
      const asin = r.asin || '—';
      if (!byAsin.has(asin)) byAsin.set(asin, { asin, skus: [], surplus_total: 0 });
      byAsin.get(asin).skus.push(r);
      byAsin.get(asin).surplus_total += r.surplus_units;
    }
    return Array.from(byAsin.values()).sort((a, b) => b.surplus_total - a.surplus_total);
  })() : null;

  const toggle = (asin) => setExpanded((s) => {
    const n = new Set(s);
    n.has(asin) ? n.delete(asin) : n.add(asin);
    return n;
  });

  return (
    <div>
      {assumptions.lead_time_missing && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          No lead time set — excess is being computed against a{' '}
          {assumptions.lead_time_days_fba}-day default. Set your real lead
          time on the Inventory Health tab for accurate flagging.
        </div>
      )}
      <div className={styles.tileGrid}>
        <Tile label="Excess SKUs" value={total_excess_skus} accent="warn" />
        <Tile label="Over-ordering" value={totals.over_ordering || 0}
              hint="Steady velocity, ordered too much" />
        <Tile label="Demand problem" value={totals.demand_problem || 0} accent="critical"
              hint="Falling velocity — check listing" />
        <Tile label="Unknown cause" value={totals.unknown || 0}
              hint="Not enough history to classify" />
        {totals.seasonal_suppressed > 0 && (
          <Tile label="Seasonal off-season" value={totals.seasonal_suppressed}
                hint="Suppressed until Seasonal Perf lands" />
        )}
      </div>
      <div className={styles.definition}>
        Excess = cover &gt; lead + ordering window + safety buffer
        ({assumptions.lead_time_days_fba} + {assumptions.ordering_window_days} + {assumptions.safety_buffer_days} = {assumptions.lead_time_days_fba + assumptions.ordering_window_days + assumptions.safety_buffer_days} days).
        Measured in units — no rupee floor (PRD #3).
      </div>
      <div className={styles.tableToolbar}>
        <label>
          Filter by cause:
          <select value={causeFilter} onChange={(e) => setCauseFilter(e.target.value)}>
            <option value="all">All ({rows.length})</option>
            <option value="over_ordering">Over-ordering</option>
            <option value="demand_problem">Demand problem</option>
            <option value="unknown">Unknown</option>
          </select>
        </label>
        <label>
          <input type="checkbox" checked={groupByAsin}
                 onChange={(e) => setGroupByAsin(e.target.checked)} />
          Group by ASIN
        </label>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Channel</th>
                <th>On hand</th><th>Surplus</th><th>Cover</th><th>Required</th>
                <th>Severity (cycles)</th><th>Cause</th><th>Trend</th></tr>
          </thead>
          <tbody>
            {!groupByAsin && filtered.slice(0, 200).map((r) => (
              <ExcessRow key={r.sku} r={r} />
            ))}
            {groupByAsin && grouped.slice(0, 100).map((g) => (
              <React.Fragment key={g.asin}>
                <tr className={styles.parentRow} onClick={() => toggle(g.asin)}>
                  <td colSpan={4} className={styles.mono}>
                    {expanded.has(g.asin) ? '▾' : '▸'} {g.asin} ({g.skus.length} SKU{g.skus.length === 1 ? '' : 's'})
                  </td>
                  <td className={styles.valueBold}>{g.surplus_total}</td>
                  <td colSpan={5} />
                </tr>
                {expanded.has(g.asin) && g.skus.map((r) => (
                  <ExcessRow key={r.sku} r={r} indent />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExcessRow = ({ r, indent }) => (
  <tr>
    <td className={styles.mono}>{indent && '  └ '}{r.sku}</td>
    <td className={styles.mono}>{r.asin || '—'}</td>
    <td>{r.fulfillment_channel}</td>
    <td>{r.on_hand}</td>
    <td className={styles.valueBold}>{r.surplus_units}</td>
    <td>{r.cover_days}d</td>
    <td>{r.required_cover_days}d</td>
    <td>{r.severity_cycles}×</td>
    <td><span className={`${styles.chip} ${causeChipCls(r.cause)}`}>
      {r.cause.replace('_', ' ')}
    </span></td>
    <td className={styles.trend}>
      {r.trend === 'up' && '↑'} {r.trend === 'down' && '↓'}
      {r.trend === 'flat' && '→'} {r.trend === 'unknown' && '?'}
    </td>
  </tr>
);

// ── Stranded ───────────────────────────────────────────────────────────────

const StrandedPanel = () => {
  const { loading, data, error } = useEndpoint(api.getStranded);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const { total_stranded_skus, total_units_stranded, total_inbound_to_broken_listings, rows } = data;
  return (
    <div>
      <div className={styles.tileGrid}>
        <Tile label="Stranded SKUs" value={total_stranded_skus} accent="critical" />
        <Tile label="Units stranded" value={total_units_stranded.toLocaleString('en-IN')} />
        {total_inbound_to_broken_listings > 0 && (
          <Tile label="Inbound to broken listings" value={total_inbound_to_broken_listings}
                accent="warn"
                hint="Stock still en route to a broken listing" />
        )}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Amazon's reason</th>
                <th>Units</th><th>Days stranded</th>
                <th title="Sort key — after this date the loss is permanent">Days to removal</th>
                <th>Storage paid</th><th>Sales forgone</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sku}>
                <td className={styles.mono}>
                  {r.sku}
                  {r.product_name && (
                    <div className={styles.rowSubtle}>{r.product_name.slice(0, 40)}</div>
                  )}
                </td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>
                  <div>{r.amazon_reason}</div>
                  {r.reason_detail && <div className={styles.rowSubtle}>{r.reason_detail}</div>}
                </td>
                <td>{r.units_stranded}
                  {r.inbound_shipped_to_broken_listing > 0 && (
                    <div className={styles.rowSubtle}>+{r.inbound_shipped_to_broken_listing} inbound</div>
                  )}
                </td>
                <td>{r.days_stranded != null ? `${r.days_stranded}d` : '—'}</td>
                <td>
                  {r.days_to_auto_removal != null ? (
                    <span className={r.days_to_auto_removal <= 30 ?
                                     styles.dangerText : ''}>
                      {r.days_to_auto_removal}d
                    </span>
                  ) : '—'}
                </td>
                <td>{r.storage_paid_while_stranded != null ?
                    `₹${r.storage_paid_while_stranded.toLocaleString('en-IN')}` : '—'}</td>
                <td>
                  {r.sales_forgone_state === 'estimated' ? (
                    <>
                      <span>₹{Math.round(r.sales_forgone_minor / 100).toLocaleString('en-IN')}</span>
                      <div className={styles.rowSubtle}>Estimated</div>
                    </>
                  ) : (
                    <span className={styles.muted}>unmeasurable</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Aging ──────────────────────────────────────────────────────────────────

const AgingPanel = () => {
  const { loading, data, error } = useEndpoint(api.getAging);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const { sku_count, total_charged_units, total_estimated_ltsf, rows, note } = data;
  return (
    <div>
      <div className={styles.tileGrid}>
        <Tile label="SKUs at/approaching charged bands" value={sku_count} accent="warn" />
        <Tile label="Units in charged bands" value={total_charged_units.toLocaleString('en-IN')} />
        <Tile label="Estimated LTSF next charge" value={`₹${total_estimated_ltsf.toLocaleString('en-IN')}`}
              accent={total_estimated_ltsf > 0 ? 'warn' : null} />
      </div>
      <div className={styles.definition}>{note}</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Bands (units)</th>
                <th>Charged units</th>
                <th>Base storage / mo</th><th>LTSF next</th><th>Total est.</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sku}>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>
                  {r.bands.map((b) => (
                    <span key={b.band} className={`${styles.bandChip} ${b.is_charged ? styles.bandCharged : ''}`}>
                      {b.band}: {b.units}
                    </span>
                  ))}
                </td>
                <td>{r.charged_units}</td>
                <td>₹{r.estimated_storage_next_month.toLocaleString('en-IN')}</td>
                <td>₹{r.estimated_ltsf_next_charge.toLocaleString('en-IN')}</td>
                <td className={styles.valueBold}>
                  ₹{r.estimated_total_cost_next_month.toLocaleString('en-IN')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Sell-Through ───────────────────────────────────────────────────────────

const SellThroughPanel = () => {
  const { loading, data, error } = useEndpoint(api.getSellThrough);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const { sku_count, with_value, rows, note } = data;
  return (
    <div>
      <div className={styles.tileGrid}>
        <Tile label="SKUs with Amazon rate" value={`${with_value} / ${sku_count}`} />
      </div>
      <div className={styles.definition}>{note}</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Sell-through (90d)</th><th>Source</th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r) => (
              <tr key={r.sku}>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>{r.sell_through_rate != null ? r.sell_through_rate.toFixed(2) : '—'}</td>
                <td className={styles.rowSubtle}>{r.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Storage Cost ───────────────────────────────────────────────────────────

const StoragePanel = () => {
  const { loading, data, error } = useEndpoint(api.getStorage);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const { next_month_total_bill, month_of_charge, surcharge_share, base_storage_total,
          lts_surcharge_total, rows, note } = data;
  return (
    <div>
      <div className={styles.tileGrid}>
        <Tile label={month_of_charge ? `Storage fees · ${month_of_charge}` : 'Next-month bill'}
              value={`₹${next_month_total_bill.toLocaleString('en-IN')}`} accent="warn" />
        <Tile label="Base storage" value={`₹${base_storage_total.toLocaleString('en-IN')}`} />
        <Tile label="Long-term surcharge" value={`₹${lts_surcharge_total.toLocaleString('en-IN')}`}
              accent={lts_surcharge_total > 0 ? 'critical' : null} />
        <Tile label="Surcharge share" value={`${Math.round(surcharge_share * 100)}%`} />
      </div>
      <div className={styles.definition}>{note}</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU / FNSKU</th><th>ASIN</th><th>Avg qty on-hand</th>
                <th>Volume</th><th>FC</th>
                <th>Base fee</th><th>LTS fee</th><th>Total est.</th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r, i) => (
              <tr key={`${r.sku || r.fnsku}-${i}`}>
                <td className={styles.mono}>{r.sku || r.fnsku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>{r.avg_qty_on_hand != null ? r.avg_qty_on_hand.toFixed(1) : '—'}</td>
                <td>{r.item_volume != null ? r.item_volume.toFixed(3) : '—'}</td>
                <td className={styles.rowSubtle}>{r.fulfillment_center || '—'}</td>
                <td>₹{r.estimated_base_storage_fee.toLocaleString('en-IN')}</td>
                <td>
                  {r.estimated_lts_fee > 0
                    ? <span className={styles.dangerText}>₹{r.estimated_lts_fee.toLocaleString('en-IN')}</span>
                    : '—'}
                </td>
                <td className={styles.valueBold}>₹{r.estimated_total.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', Component: OverviewPanel },
  { id: 'excess', label: 'Excess', Component: ExcessPanel },
  { id: 'stranded', label: 'Stranded', Component: StrandedPanel },
  { id: 'aging', label: 'Aging', Component: AgingPanel },
  { id: 'sell-through', label: 'Sell-Through', Component: SellThroughPanel },
  { id: 'storage', label: 'Storage', Component: StoragePanel },
];

const InventoryPerformance = () => {
  const authCtx = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const activeTab = searchParams.get('tab') || 'overview';
  const causeFilter = searchParams.get('cause') || undefined;
  const active = TABS.find((t) => t.id === activeTab) || TABS[0];
  const ActiveComponent = active.Component;

  const hardRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 'overview' && authCtx?.token) {
        await api.getOverview(authCtx.token, { refresh: true });
      }
    } catch {
      // best-effort
    } finally {
      setReloadKey((k) => k + 1);
      setRefreshing(false);
    }
  };

  const goToTab = (tabId, opts = {}) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    if (opts.cause) next.set('cause', opts.cause);
    else next.delete('cause');
    setSearchParams(next);
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Inventory Performance Metrics</h2>
          <p className={styles.pageSubtitle}>
            Amazon's own reports + Inventory Health's classifications.
            Nothing inferred here.
          </p>
        </div>
        <button className={styles.btnGhost || ''}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db',
                         borderRadius: 4, background: '#fff',
                         cursor: refreshing ? 'wait' : 'pointer' }}
                onClick={hardRefresh} disabled={refreshing}>
          {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
        </button>
      </div>
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id}
            className={`${styles.tab} ${t.id === activeTab ? styles.tabActive : ''}`}
            onClick={() => goToTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className={styles.tabBody}
           key={`${activeTab}-${causeFilter || 'all'}-${reloadKey}`}>
        <ActiveComponent onTabClick={goToTab} initialCauseFilter={causeFilter} />
      </div>
    </div>
  );
};

export default InventoryPerformance;
